import { logger } from '@luna/logger';

import type { RecentTransactionTimes } from './LatestTransactionCheckApi';
import { queryForRecentTransactionTimes } from './LatestTransactionCheckApi';
import { filterUnmatchedEntries } from './filterUnmatchedEntries';
import { filterExistingEntries } from './filterExistingEntries';

type NewTransactionsPollingType = {
    accountId: string | undefined;
    lastInvocation: number | undefined;
    timer: ReturnType<typeof setTimeout> | undefined;
};

/**
 * A minimum number of milliseconds that must elapse between polling requests.
 * This is used to limit the load on the service, especially when requests are
 * failing.
 */
const MINIMUM_POLLING_INTERVAL = 15_000;

const NewTransactionsPolling: NewTransactionsPollingType = {
    accountId: undefined,
    lastInvocation: undefined,
    timer: undefined,
};

const cache: Record<'accounts', RecentTransactionTimes> = {
    accounts: {},
};

const Polling = {
    cache,
    MINIMUM_POLLING_INTERVAL,
    NewTransactionsPolling,
    poll,
    pollForNewTransactions,
};

export default Polling;

export function pollForNewTransactions(
    accountId: string,
    onNewTransaction: () => unknown,
): () => void {
    startPolling(accountId, onNewTransaction);
    return stopPolling;
}

function poll(onNewTransaction: () => unknown): void {
    clearTimeout(NewTransactionsPolling.timer);
    const invoke = async (accountId?: string): Promise<void> => {
        NewTransactionsPolling.lastInvocation = Date.now();
        try {
            const hasNewTransactions = await checkForNewRecentTransactions(accountId);
            const wasPollingAborted = isStopped();
            const isPollingResultObsolete = accountId !== NewTransactionsPolling.accountId;
            if (wasPollingAborted || isPollingResultObsolete) {
                return;
            }
            if (hasNewTransactions) {
                onNewTransaction();
            }
            Polling.poll(onNewTransaction);
        } catch (error) {
            logger.error(error);
            stopPolling();
        }
    };
    const delay = getDelay(NewTransactionsPolling.lastInvocation, MINIMUM_POLLING_INTERVAL);
    const { accountId } = NewTransactionsPolling;
    NewTransactionsPolling.timer = setTimeout(() => invoke(accountId), delay);
}

function startPolling(accountId: string, onNewTransaction: () => unknown): void {
    NewTransactionsPolling.accountId = accountId;
    if (isStopped()) {
        Polling.poll(onNewTransaction);
    }
}

function stopPolling(): void {
    clearTimeout(NewTransactionsPolling.timer);
    NewTransactionsPolling.timer = undefined;
}

function isStopped(): boolean {
    return NewTransactionsPolling.timer === undefined;
}

function getDelay(timeOfLastCall: number | undefined, minimumCallInterval: number): number {
    if (timeOfLastCall === undefined) {
        return 0;
    }
    const elapsed = Date.now() - timeOfLastCall;
    return Math.max(0, minimumCallInterval - elapsed);
}

async function checkForNewRecentTransactions(accountId?: string): Promise<boolean> {
    const changes = await fetchNewRecentTransactionTimes(Polling.cache.accounts, accountId);
    Polling.cache.accounts = { ...cache.accounts, ...changes };
    return Object.keys(changes).length > 0;
}

async function fetchNewRecentTransactionTimes(
    oldTimes: RecentTransactionTimes,
    accountId?: string,
): Promise<RecentTransactionTimes> {
    const newTimes = await queryForRecentTransactionTimes({ accountId });
    const changes = filterUnmatchedEntries(oldTimes, newTimes);
    const interestingChanges = filterExistingEntries(changes, accountId ? [accountId] : undefined);
    return removeUndefinedEntries(interestingChanges);
}

function removeUndefinedEntries<T extends object>(obj: object): T {
    return Object.entries(obj).reduce(
        (acc, [key, value]) => (typeof value === 'undefined' ? acc : { ...acc, [key]: value }),
        {} as T,
    );
}
